import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Icon } from '@iconify/react'
import { Check } from 'lucide-react'

import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout.jsx'
import { AuthInput, AuthButton, AuthChip, MicroLabel } from '@/components/auth/AuthPrimitives.jsx'

// Presentational onboarding flow: a single light, progressive page. Each answer
// reveals the next row. Pure/props-driven so it can be previewed without the
// dashboard context tree; OnboardingPage wires the real side-effects in.

const SLIDE = {
    id: 'onboarding',
    glow: 'a',
    eyebrow: 'Welcome',
    title: 'Set up your creative context.',
    subtitle: 'A few quick answers and your AI is grounded in your world.',
}

const INTENTS = [
    { id: 'competitors', label: 'Research competitors' },
    { id: 'discover', label: 'Find new advertisers' },
    { id: 'enrich', label: 'Enrich advertiser data' },
    { id: 'trends', label: 'Market & creative trends' },
    { id: 'track', label: 'Track specific brands' },
    { id: 'own_ads', label: 'Analyze my own ads' },
    { id: 'exploring', label: 'Just exploring' },
]

const USAGE = [
    { id: 'platform', label: 'The platform' },
    { id: 'mcp', label: 'Claude (MCP)' },
    { id: 'api', label: 'The API' },
]

// Multi-select chip group with an optional "Other" chip that reveals a text
// input when picked.
const ChipGroup = ({ options, value, onToggle, allowOther = false, otherValue = '', onOtherChange, otherPlaceholder = 'Tell us more' }) => {

    const otherSelected = allowOther && value.includes('other')

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
                {options.map((opt) => (
                    <AuthChip key={opt.id} selected={value.includes(opt.id)} onClick={() => onToggle(opt.id)}>
                        {opt.label}
                    </AuthChip>
                ))}
                {allowOther && (
                    <AuthChip selected={otherSelected} onClick={() => onToggle('other')}>
                        Other
                    </AuthChip>
                )}
            </div>
            <AnimatePresence>
                {otherSelected && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                        <AuthInput
                            autoFocus
                            placeholder={otherPlaceholder}
                            value={otherValue}
                            onChange={(e) => onOtherChange(e.target.value)}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

const Row = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="flex flex-col gap-3"
    >
        {children}
    </motion.div>
)

export function OnboardingFlow({
    firstName,
    isGenericEmail = false,
    metaIntegration = null,
    onConnectMeta = () => { },
    onFinish = () => { },
    onEvent = () => { },
    onLogout,
    isFinishing = false,
}) {

    const [intent, setIntent] = useState([])
    const [intentOther, setIntentOther] = useState('')
    const [website, setWebsite] = useState('')
    const [usage, setUsage] = useState([])
    const [notNow, setNotNow] = useState(false)
    // stage 0: intent · 1: connect (+ website) · 2: usage + finish
    const [stage, setStage] = useState(0)

    // First intent pick reveals the connect row.
    useEffect(() => {
        if (intent.length > 0) {
            setStage((s) => Math.max(s, 1))
        }
    }, [intent.length])

    // Connecting Meta (or choosing "Not now") reveals the final section.
    useEffect(() => {
        if (stage === 1 && (metaIntegration || notNow)) {
            setStage(2)
        }
    }, [stage, metaIntegration, notNow])

    useEffect(() => {
        onEvent('onboarding-shown')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const toggleIntent = (id) => {
        onEvent('onboarding-intent-selected', { intent: id, selected: !intent.includes(id) })
        setIntent((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
    }

    const toggleUsage = (id) => {
        onEvent('onboarding-usage-selected', { usage: id, selected: !usage.includes(id) })
        setUsage((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
    }

    const handleNotNow = () => {
        onEvent('onboarding-meta-skipped')
        setNotNow(true)
    }

    // "Other" only counts as an answered intent once it has text. Deselecting
    // every intent leaves the rows in place but blocks finishing.
    const hasIntent = intent.some((i) => i !== 'other') || (intent.includes('other') && intentOther.trim().length > 0)
    const canFinish = hasIntent && usage.length > 0

    const handleFinish = () => {
        if (!canFinish) {
            return
        }
        onFinish({
            intent,
            intentOther: intent.includes('other') ? intentOther.trim() : '',
            usage,
            website: website.trim(),
        })
    }

    return (
        <SplitAuthLayout
            slide={SLIDE}
            align="top"
            maxWidth={600}
            footer={onLogout ? (
                <button type="button" onClick={onLogout} className="text-[13px] text-auth-muted underline-offset-4 hover:text-auth-ink hover:underline">
                    Log out
                </button>
            ) : null}
        >
            <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                    <MicroLabel>Welcome{firstName ? `, ${firstName}` : ''}</MicroLabel>
                    <h1 className="text-3xl font-light leading-[1.1] tracking-[-0.02em] text-auth-ink">
                        Let us tailor Upspring to you
                    </h1>
                </div>

                {/* Row 1 — intent (multi-select) */}
                <Row>
                    <label className="text-[13px] font-medium text-auth-ink">
                        What did you come here to do? <span className="font-normal text-auth-muted">(select all that apply)</span>
                    </label>
                    <ChipGroup
                        options={INTENTS}
                        value={intent}
                        onToggle={toggleIntent}
                        allowOther
                        otherValue={intentOther}
                        onOtherChange={setIntentOther}
                        otherPlaceholder="What are you here to do?"
                    />
                    {stage >= 1 && !hasIntent && (
                        <span className="text-[12px] text-auth-muted">Pick at least one, or tell us your own.</span>
                    )}
                </Row>

                <AnimatePresence>
                    {stage >= 1 && (
                        <Row key="connect">
                            {isGenericEmail && (
                                <div className="flex flex-col gap-2">
                                    <label className="text-[13px] font-medium text-auth-ink">
                                        Your website <span className="font-normal text-auth-muted">(optional)</span>
                                    </label>
                                    <AuthInput
                                        type="url"
                                        placeholder="https://yourbrand.com"
                                        value={website}
                                        onChange={(e) => setWebsite(e.target.value)}
                                    />
                                </div>
                            )}

                            <label className="text-[13px] font-medium text-auth-ink">
                                Connect your ad account <span className="font-normal text-auth-muted">(optional)</span>
                            </label>
                            {!metaIntegration ? (
                                <div className="flex items-center gap-3">
                                    <AuthButton variant="secondary" onClick={onConnectMeta}>
                                        <Icon icon="mdi:facebook" className="h-5 w-5" />
                                        Connect Meta
                                    </AuthButton>
                                    <button
                                        type="button"
                                        onClick={handleNotNow}
                                        className="text-sm text-auth-muted underline-offset-4 hover:text-auth-ink hover:underline"
                                    >
                                        Not now
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="inline-flex items-center gap-1.5 text-sm text-auth-ink">
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-auth-accent text-white">
                                            <Check className="h-3 w-3" strokeWidth={3} />
                                        </span>
                                        Meta connected
                                    </span>
                                </div>
                            )}
                        </Row>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {stage >= 2 && (
                        <Row key="usage">
                            <label className="text-[13px] font-medium text-auth-ink">How would you like to use Upspring?</label>
                            <ChipGroup options={USAGE} value={usage} onToggle={toggleUsage} />
                        </Row>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {stage >= 2 && (
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: 'easeOut' }}>
                            <AuthButton variant="primary" className="w-full sm:w-auto" onClick={handleFinish} disabled={!canFinish || isFinishing}>
                                Enter Upspring
                            </AuthButton>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </SplitAuthLayout>
    )
}
